/* eslint-disable @typescript-eslint/ban-ts-comment */
// modules
import { addModal, closeCurrentModal } from 'weblite-web-modal-viewer'
import { openFile } from '@weblite/file'
import { styled } from '@mui/material/styles'
import { WappPanel } from '$logic/wapp-panels'
import {
  ContactsOffIcon,
  CameraOffIcon,
  MutedMicIcon,
  NotificationsOffIcon,
  MicActiveIcon,
  CameraActiveIcon,
  ContactIcon,
  NotificationActiveIcon,
} from '$common/icons'
import { t } from '$i18n'
import { registerNotification } from '$setup/notification'
import { dispatchSetPermissions } from '$comp/main/root.action'
import { RefreshIcon } from '$common/icons/refresh'
import { ClockAlertIcon } from '$common/icons/clock-alert'
import { checkTimeOffset } from '$logic/client-device/time-offset.service'
import { isLoggedIn } from '$logic/auth'
import { StartUpModalQueue } from '$func/startup-modal-handler'
import { StartupModalPriorities } from '$helper/constants/startup-modal-priorities'
import { PermissionDialog } from './permission-dialog'
import { setLastNotifPermissionRejectionTime } from '$setup/notification/permission'

const COMPONENT_ID = 'permissionDialog'

const showPermissionTour = (props: Parameters<typeof PermissionDialog>[0]) =>
  addModal({ id: COMPONENT_ID, props })

const BLOGS = {
  // @ts-ignore
  clockMismatch: import.meta.env.VITE_CLOCK_MISMATCH_CONTENT_URI,
  // @ts-ignore
  notification: import.meta.env
    .VITE_ENABLE_NOTIFICATION_INSTRUCTION_CONTENT_URI,
}

export const showNotificationsPermissionPromptDialog = () => {
  showPermissionTour({
    title: t('permissionModal.promptTitle')(t(`permissionModal.notifications`)),
    description: t('permissionModal.promptDescription')(
      t(`permissionModal.notifications`),
    ),
    icon: <NotificationActiveIcon />,
    promptMode: true,
    firstActionText: t('common.resume'),
    secondActionText: t('common.cancel'),
    onSecondAction: async () => {
      setLastNotifPermissionRejectionTime()
      await closeCurrentModal()
    },
    onFirstAction: async () => {
      registerNotification()
      await closeCurrentModal()
    },
  })
}

const StyledContactsIcon = styled(ContactIcon)`
  width: 64px;
  height: 64px;
`
export const showContactsPermissionPromptDialog = () => {
  showPermissionTour({
    title: t('permissionModal.promptTitle')(t(`permissionModal.contacts`)),
    description: t('permissionModal.promptDescription')(
      t(`permissionModal.contacts`),
    ),
    firstActionText: t('common.resume'),
    secondActionText: t('common.cancel'),
    icon: <StyledContactsIcon />,
    promptMode: true,
    onSecondAction: async () => {
      await closeCurrentModal()
    },
    onFirstAction: async () => {
      await closeCurrentModal()
      // TODO:
    },
  })
}
export const showCameraPermissionPromptDialog = () => {
  showPermissionTour({
    title: t('permissionModal.promptTitle')(t(`permissionModal.camera`)),
    description: t('permissionModal.promptDescription')(
      t(`permissionModal.camera`),
    ),
    firstActionText: t('common.resume'),
    secondActionText: t('common.cancel'),
    icon: <CameraActiveIcon />,
    promptMode: true,
    onSecondAction: async () => {
      await closeCurrentModal()
    },
    onFirstAction: async () => {
      await closeCurrentModal()
      // TODO:
    },
  })
}

export const showMicPermissionPromptDialog = () => {
  showPermissionTour({
    title: t('permissionModal.promptTitle')(t(`permissionModal.mic`)),
    description: t('permissionModal.promptDescription')(
      t(`permissionModal.mic`),
    ),
    firstActionText: t('common.resume'),
    secondActionText: t('common.cancel'),
    icon: <MicActiveIcon />,
    promptMode: true,
    onSecondAction: async () => {
      await closeCurrentModal()
    },
    onFirstAction: async () => {
      await closeCurrentModal()
      navigator.mediaDevices
        .getUserMedia?.({ audio: true })
        .then(stream => {
          dispatchSetPermissions({ mic: 'granted' })
          stream.getTracks().forEach(track => {
            track.stop()
          })
        })
        .catch(() => dispatchSetPermissions({ mic: 'denied' }))
    },
  })
}

export const showRecommendUpdateTwaDialog = () => {
  // @ts-ignore
  const downloadSrc = import.meta.env.VITE_TWA_DOWNLOAD_SRC
  if (!downloadSrc) return
  showPermissionTour({
    title: t('permissionModal.updateTwaVersion.title'),
    description: t('permissionModal.updateTwaVersion.description'),
    firstActionText: t('permissionModal.updateTwaVersion.confirmOption'),
    secondActionText: t('permissionModal.updateTwaVersion.cancelOption'),
    icon: <RefreshIcon />,
    promptMode: true,
    onSecondAction: async () => {
      await closeCurrentModal()
    },
    onFirstAction: async () => {
      openFile(downloadSrc, '', true)

      await closeCurrentModal()
    },
  })
}

const CLOCK_MISMATCH_INFOS = {
  wappId: '60a772ca526d2ee0f383a6e7',
  input: {
    articleId: '33dfc04a-ba21-47a8-810e-5ae1eed00c38',
    instanceId: 'fd6c1887-8f53-4300-bb85-68d58a8b9e09',
  },
}

export const showClockMismatchDialog = () => {
  showPermissionTour({
    title: t('permissionModal.clockMismatch.title'),
    description: t('permissionModal.clockMismatch.description'),
    firstActionText: t('permissionModal.infoOption'),
    icon: <ClockAlertIcon />,
    promptMode: false,
    onSecondAction: async () => {
      await closeCurrentModal()
    },
    onFirstAction: async () => {
      await closeCurrentModal()
      WappPanel.openWapp(CLOCK_MISMATCH_INFOS)
    },
  })
}

export const checkClockMismatch = () => {
  checkTimeOffset()
  // if (BLOGS.clockMismatch && isLoggedIn() && (await shouldShowClockDialog())) {
  //   StartUpModalQueue.add({
  //     priority: StartupModalPriorities.clockMismatch,
  //     onOpen: showClockMismatchDialog,
  //   })
  // }
}

export const showNotificationsPermissionDeniedDialog = () => {
  showPermissionTour({
    title: t('permissionModal.deniedTitle')(t(`permissionModal.notifications`)),
    description: t('permissionModal.deniedDescription')(
      t(`permissionModal.notifications`),
    ),
    firstActionText: t('common.understand'),
    secondActionText: t('permissionModal.infoOption'),
    icon: <NotificationsOffIcon />,
    promptMode: false,
    onSecondAction: async () => {
      await closeCurrentModal()
      WappPanel.openWapp({
        wappId: 'blog-blog-main',
        input: { contentUri: BLOGS.notification },
      })
    },
    onFirstAction: async () => {
      await closeCurrentModal()
    },
  })
}

export const showContactsPermissionDeniedDialog = () => {
  showPermissionTour({
    title: t('permissionModal.deniedTitle')(t(`permissionModal.contacts`)),
    description: t('permissionModal.deniedDescription')(
      t(`permissionModal.contacts`),
    ),
    firstActionText: t('common.understand'),
    secondActionText: t('permissionModal.infoOption'),
    icon: <ContactsOffIcon />,
    promptMode: false,
    onSecondAction: async () => {
      await closeCurrentModal()
      WappPanel.openWapp(CONTACT_DENIED_DIALOG)
    },
    onFirstAction: async () => {
      await closeCurrentModal()
    },
  })
}

export const showCameraPermissionDeniedDialog = () => {
  showPermissionTour({
    title: t('permissionModal.deniedTitle')(t(`permissionModal.camera`)),
    description: t('permissionModal.cameraDeniedDescription'),
    firstActionText: t('common.understand'),
    icon: <CameraOffIcon />,
    promptMode: false,
    onFirstAction: async () => {
      await closeCurrentModal()
    },
  })
}

const StyledMicIcon = styled(MutedMicIcon)(({ theme }) => ({
  color: theme.palette.error.main,
  width: '64px',
  height: '64px',
}))

const MIC_DENIED_INFOS = {
  wappId: '60a772ca526d2ee0f383a6e7',
  input: {
    articleId: '3b28ef65-8d44-42ab-ba43-7f1f207ad03c',
    instanceId: 'd4ef6272-c9fd-40db-bbda-d3dd08366c4a',
  },
}
const CONTACT_DENIED_DIALOG = {
  wappId: '609c2b38526d2ee0f383a6df',
  input: {
    articleId: 'a24ed33c-17c3-40e7-b775-3e63222a0a8e',
    instanceId: 'a784a47d-c059-420c-a4c7-b0ed880d0aff',
  },
}

export const showMicPermissionDeniedDialog = () => {
  showPermissionTour({
    title: t('permissionModal.deniedTitle')(t(`permissionModal.mic`)),
    description: t('permissionModal.deniedDescription')(
      t(`permissionModal.mic`),
    ),
    firstActionText: t('common.understand'),
    secondActionText: t('permissionModal.infoOption'),
    icon: <StyledMicIcon />,
    promptMode: false,
    onSecondAction: async () => {
      await closeCurrentModal()
      WappPanel.openWapp(MIC_DENIED_INFOS)
    },
    onFirstAction: async () => {
      await closeCurrentModal()
    },
  })
}

export default {
  [COMPONENT_ID]: {
    Component: PermissionDialog,
  },
}
